import { createClient } from '@supabase/supabase-js';
import { officeRoamingStorage } from './roamingStorage';

/**
 * Supabase client for the add-in.
 *
 * The URL and publishable key are PUBLIC by design — the publishable key is
 * RLS-gated and ships in the main SPA bundle too. No service key ever comes
 * near this app.
 *
 * Session storage is Office roaming settings (see roamingStorage.ts) so the
 * user stays signed in across Outlook restarts and devices.
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? 'https://ucjtxdmklhhhvayirwqe.supabase.co';
const SUPABASE_KEY =
  import.meta.env.VITE_SUPABASE_ANON_KEY ?? 'sb_publishable_X9vNmbUQJG1TiesVlOvK4g_oeSxdxep';

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    storage: officeRoamingStorage,
    storageKey: 'flowza-addin-auth',
    persistSession: true,
    autoRefreshToken: true,
    // A taskpane is not a redirect target: there is no URL bar and the iframe
    // cannot receive an OAuth callback. All sign-in happens in-pane.
    detectSessionInUrl: false,
  },
});

/** Map auth errors to something safe to show a user (Absolute Rule #5). */
export function friendlyAuthError(message: string | undefined): string {
  const m = (message ?? '').toLowerCase();
  if (m.includes('invalid login credentials')) return 'That email or password is not right.';
  if (m.includes('email not confirmed')) return 'Confirm your email address first, then sign in.';
  if (m.includes('token has expired') || m.includes('expired')) return 'That code has expired — send a new one.';
  // MUST precede the `otp` branch below. `shouldCreateUser: false` on an address
  // with no Flowza account returns otp_disabled, whose message is "Sign in with
  // OTPs (magic link, email OTP) is disabled" — it contains "otp", so the generic
  // branch would claim the code is invalid when no code was ever sent.
  if (m.includes('disabled') || m.includes('signups not allowed') || m.includes('sign ups')) {
    return 'That address has no Flowza account, or code sign-in is switched off.';
  }
  if (m.includes('not found') || m.includes('no longer exists')) return 'That address has no Flowza account.';
  if (m.includes('invalid token') || m.includes('otp')) return 'That code is not valid. Check it and try again.';
  if (m.includes('rate limit') || m.includes('too many')) return 'Too many attempts. Wait a minute and try again.';
  if (m.includes('failed to fetch') || m.includes('network')) return 'Cannot reach Flowza. Check your connection.';
  return 'Sign-in failed. Please try again.';
}
